export interface OrderPaymentSummary {
  orderCount: number;
  itemCount: number;
  productAmount: number;
  shippingAmount: number;
  discountAmount: number;
  couponAmount: number;
  /**
   * Cafe24 report-compatible order composition amount. This is intentionally
   * separate from `paymentAmount`, which is the order's current payment amount.
   */
  orderCompositionAmount: number;
  paymentAmount: number;
  orderPaymentAmount: number;

  // Initial (최초) breakdown values
  initialOrderCount: number;
  initialItemCount: number;
  initialProductAmount: number;
  initialOrderPriceAmount: number;
  initialShippingAmount: number;
  initialDiscountAmount: number;
  initialCouponAmount: number;
  initialOrderCompositionAmount: number;
  initialPaymentAmount: number;

  // Actual (현재) breakdown values
  actualOrderCount: number;
  actualItemCount: number;
  actualProductAmount: number;
  actualOrderPriceAmount: number;
  actualShippingAmount: number;
  actualDiscountAmount: number;
  actualCouponAmount: number;
  actualOrderCompositionAmount: number;
  actualPaymentAmount: number;
}

const EMPTY_SUMMARY: OrderPaymentSummary = {
  orderCount: 0,
  itemCount: 0,
  productAmount: 0,
  shippingAmount: 0,
  discountAmount: 0,
  couponAmount: 0,
  orderCompositionAmount: 0,
  paymentAmount: 0,
  orderPaymentAmount: 0,

  initialOrderCount: 0,
  initialItemCount: 0,
  initialProductAmount: 0,
  initialOrderPriceAmount: 0,
  initialShippingAmount: 0,
  initialDiscountAmount: 0,
  initialCouponAmount: 0,
  initialOrderCompositionAmount: 0,
  initialPaymentAmount: 0,

  actualOrderCount: 0,
  actualItemCount: 0,
  actualProductAmount: 0,
  actualOrderPriceAmount: 0,
  actualShippingAmount: 0,
  actualDiscountAmount: 0,
  actualCouponAmount: 0,
  actualOrderCompositionAmount: 0,
  actualPaymentAmount: 0,
};

function toAmount(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !value.trim()) return undefined;
  const parsed = Number(value.replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : undefined;
}

function readAmount(source: unknown, keys: string[]): number | undefined {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return undefined;
  }
  const value = source as Record<string, unknown>;
  for (const key of keys) {
    const amount = toAmount(value[key]);
    if (amount !== undefined) return Math.max(0, amount);
  }
  return undefined;
}

function sumAmounts(source: unknown, keys: string[]): number {
  if (!source || typeof source !== "object" || Array.isArray(source)) return 0;
  const value = source as Record<string, unknown>;
  return keys.reduce((sum, key) => sum + (toAmount(value[key]) || 0), 0);
}

const CANCELLED_OR_REFUNDED_ORDER_STATUSES = new Set([
  "C35", // 취소처리중 - 환불완료
  "C40", // 취소완료
  "C41", // 취소 완료 - 환불전
  "C42", // 취소 완료 - 환불요청중
  "C43", // 취소 완료 - 환불보류
  "C47", // 입금전취소 - 구매자
  "C48", // 입금전취소 - 자동취소
  "C49", // 입금전취소 - 관리자
  "R40", // 반품완료 - 환불완료
  "R41", // 반품완료 - 환불전
  "R42", // 반품완료 - 환불요청중
  "R43", // 반품완료 - 환불보류
]);

function isItemCancelled(item: any): boolean {
  if (!item) return false;
  const status = String(item.order_status || "").toUpperCase();
  return CANCELLED_OR_REFUNDED_ORDER_STATUSES.has(status);
}

function sumItemProductAmounts(items: any[]): number {
  return items.reduce(
    (sum: number, item: any) =>
      sum +
      (toAmount(item.product_price) || 0) * (toAmount(item.quantity) || 1) +
      (toAmount(item.option_price) || 0) * (toAmount(item.quantity) || 1),
    0
  );
}

interface OrderAmountBreakdown {
  productAmount: number;
  shippingAmount: number;
  discountAmount: number;
  couponAmount: number;
  orderCompositionAmount: number;
  paymentAmount: number;
}

function calculateOrderAmount(
  amountSource: unknown,
  isKakaoPay: boolean,
  items: any[] = []
): OrderAmountBreakdown {
  const orderPrice = readAmount(amountSource, ["order_price_amount"]);
  const itemsProductAmount = sumItemProductAmounts(items);
  let productAmount =
    itemsProductAmount > 0 ? itemsProductAmount : orderPrice ?? 0;

  const shippingAmount =
    (readAmount(amountSource, ["shipping_fee"]) ?? 0) +
    sumAmounts(amountSource, [
      "additional_shipping_fee",
      "international_shipping_insurance",
      "additional_handling_fee",
    ]);

  const baseCoupon =
    readAmount(amountSource, [
      "coupon_discount_price",
      "coupon_discount_amount",
    ]) ?? 0;
  const shippingCoupon =
    readAmount(amountSource, ["coupon_shipping_fee_amount"]) ?? 0;
  let couponAmount = baseCoupon + shippingCoupon;

  if (couponAmount === 0 && items.length > 0) {
    couponAmount = items.reduce(
      (sum: number, item: any) =>
        sum +
        (toAmount(item.coupon_discount_price) ||
          toAmount(item.coupon_discount_amount) ||
          0),
      0
    );
  }

  const orderLevelDiscounts = sumAmounts(amountSource, [
    "membership_discount_amount",
    "shipping_fee_discount_amount",
    "set_product_discount_amount",
    "app_discount_amount",
    "market_other_discount_amount",
    "order_discount_amount",
    "item_discount_amount",
  ]);
  const itemDiscounts = items.reduce(
    (sum: number, item: any) =>
      sum +
      (toAmount(item.additional_discount_price) || 0) +
      (toAmount(item.app_item_discount_amount) || 0) +
      (toAmount(item.market_discount_amount) || 0) +
      (toAmount(item.order_discount_amount) || 0) +
      (toAmount(item.item_discount_amount) || 0),
    0
  );
  const totalDiscount = readAmount(amountSource, ["total_discount_amount"]);
  const discountAmount =
    totalDiscount !== undefined
      ? Math.max(0, totalDiscount - couponAmount)
      : orderLevelDiscounts + itemDiscounts;

  const paymentAmount = readAmount(amountSource, ["payment_amount"]) ?? 0;
  if (isKakaoPay && paymentAmount > 0) {
    const expectedComposition =
      productAmount + shippingAmount - discountAmount - couponAmount;
    if (paymentAmount < expectedComposition) {
      productAmount = Math.max(
        0,
        productAmount - (expectedComposition - paymentAmount)
      );
    }
  }

  return {
    productAmount,
    shippingAmount,
    discountAmount,
    couponAmount,
    orderCompositionAmount: Math.max(
      0,
      productAmount + shippingAmount - discountAmount - couponAmount
    ),
    paymentAmount,
  };
}

/**
 * Keeps Cafe24's two money concepts separate:
 * - `orderCompositionAmount`: product + shipping - discount - coupon
 * - `paymentAmount`: Cafe24's current payment amount
 *
 * They must be displayed together, but never added together.
 */
export function summarizeOrderPayment(order: any): OrderPaymentSummary {
  if (!order || typeof order !== "object") return { ...EMPTY_SUMMARY };

  const initial = order.initial_order_amount;
  const actual = order.actual_order_amount;
  const rawItems = Array.isArray(order.items) ? order.items : [];

  const isKakaoPay =
    order?.order_place_id === "KAKAOPAY" ||
    String(order?.order_place_name || "").includes("톡체크아웃") ||
    String(order?.order_place_name || "").includes("카카오");

  const activeItems = rawItems.filter((item: any) => !isItemCancelled(item));
  const initialBreakdown = calculateOrderAmount(initial, isKakaoPay, rawItems);
  const isFullOrderCancelled =
    rawItems.length > 0 && rawItems.every(isItemCancelled);
  const actualShippingAmountForCancelledOrder = isFullOrderCancelled
    ? calculateOrderAmount(actual, isKakaoPay, []).shippingAmount
    : 0;
  const actualBreakdown = isFullOrderCancelled
    ? {
        productAmount: 0,
        // A fully cancelled order can still have a newly charged shipping fee
        // (for example after an exchange or partial cancellation/return flow).
        // Preserve that charge even though all item amounts are cancelled.
        shippingAmount: actualShippingAmountForCancelledOrder,
        discountAmount: 0,
        couponAmount: 0,
        orderCompositionAmount: Math.max(
          0,
          actualShippingAmountForCancelledOrder
        ),
        paymentAmount: 0,
      }
    : calculateOrderAmount(actual, isKakaoPay, activeItems);
  if (
    !isFullOrderCancelled &&
    rawItems.length > 0 &&
    activeItems.length < rawItems.length &&
    sumItemProductAmounts(rawItems) > 0
  ) {
    const ratio =
      actualBreakdown.productAmount / sumItemProductAmounts(rawItems);
    actualBreakdown.discountAmount = Math.round(
      initialBreakdown.discountAmount * ratio
    );
    actualBreakdown.couponAmount = Math.round(
      initialBreakdown.couponAmount * ratio
    );
    actualBreakdown.orderCompositionAmount = Math.max(
      0,
      actualBreakdown.productAmount +
        actualBreakdown.shippingAmount -
        actualBreakdown.discountAmount -
        actualBreakdown.couponAmount
    );
  }
  const initialOrderPriceAmount =
    readAmount(initial, ["order_price_amount"]) ?? 0;
  const actualOrderPriceAmount =
    readAmount(actual, ["order_price_amount"]) ?? 0;
  const orderPaymentAmount = readAmount(order, ["payment_amount"]) ?? 0;

  const initialOrderCount = 1;
  const initialItemCount = rawItems.length;
  const actualOrderCount = isFullOrderCancelled ? 0 : 1;
  const actualItemCount = activeItems.length;

  return {
    orderCount: 1,
    itemCount: rawItems.length,
    productAmount: actualBreakdown.productAmount,
    shippingAmount: actualBreakdown.shippingAmount,
    discountAmount: actualBreakdown.discountAmount,
    couponAmount: actualBreakdown.couponAmount,
    orderCompositionAmount: actualBreakdown.orderCompositionAmount,
    paymentAmount: actualBreakdown.paymentAmount,

    initialOrderCount,
    actualOrderCount,
    initialItemCount,
    actualItemCount,
    initialProductAmount: initialBreakdown.productAmount,
    initialOrderPriceAmount,
    actualProductAmount: actualBreakdown.productAmount,
    actualOrderPriceAmount,
    initialShippingAmount: initialBreakdown.shippingAmount,
    actualShippingAmount: actualBreakdown.shippingAmount,
    initialDiscountAmount: initialBreakdown.discountAmount,
    actualDiscountAmount: actualBreakdown.discountAmount,
    initialCouponAmount: initialBreakdown.couponAmount,
    actualCouponAmount: actualBreakdown.couponAmount,
    initialOrderCompositionAmount: initialBreakdown.orderCompositionAmount,
    actualOrderCompositionAmount: actualBreakdown.orderCompositionAmount,
    initialPaymentAmount: initialBreakdown.paymentAmount,
    actualPaymentAmount: actualBreakdown.paymentAmount,
    orderPaymentAmount,
  };
}

export function getOrderPaymentSummary(order: any): OrderPaymentSummary {
  const cached = order?.payment_summary;

  // Older IndexedDB rows do not have `orderCompositionAmount`, `initialProductAmount`,
  // `initialDiscountAmount`, or `initialCouponAmount`.
  // Recalculate those rows from their preserved non-PII order fields.
  if (
    toAmount(cached?.orderCompositionAmount) === undefined ||
    toAmount(cached?.initialProductAmount) === undefined ||
    toAmount(cached?.initialDiscountAmount) === undefined ||
    toAmount(cached?.actualDiscountAmount) === undefined ||
    toAmount(cached?.initialCouponAmount) === undefined ||
    toAmount(cached?.actualCouponAmount) === undefined
  ) {
    return summarizeOrderPayment(order);
  }

  const initialProductAmount =
    toAmount(cached.initialProductAmount) ??
    toAmount(cached.productAmount) ??
    0;
  const actualProductAmount =
    toAmount(cached.actualProductAmount) ?? toAmount(cached.productAmount) ?? 0;
  const initialOrderPriceAmount =
    toAmount(cached.initialOrderPriceAmount) ?? initialProductAmount;
  const actualOrderPriceAmount =
    toAmount(cached.actualOrderPriceAmount) ?? actualProductAmount;
  const initialShippingAmount =
    toAmount(cached.initialShippingAmount) ??
    toAmount(cached.shippingAmount) ??
    0;
  const actualShippingAmount =
    toAmount(cached.actualShippingAmount) ??
    toAmount(cached.shippingAmount) ??
    0;
  const initialDiscountAmount =
    toAmount(cached.initialDiscountAmount) ??
    toAmount(cached.discountAmount) ??
    0;
  const actualDiscountAmount =
    toAmount(cached.actualDiscountAmount) ??
    toAmount(cached.discountAmount) ??
    0;
  const initialCouponAmount =
    toAmount(cached.initialCouponAmount) ?? toAmount(cached.couponAmount) ?? 0;
  const actualCouponAmount =
    toAmount(cached.actualCouponAmount) ?? toAmount(cached.couponAmount) ?? 0;
  const initialOrderCount =
    toAmount(cached.initialOrderCount) ?? toAmount(cached.orderCount) ?? 1;
  const actualOrderCount =
    toAmount(cached.actualOrderCount) ?? toAmount(cached.orderCount) ?? 1;
  const initialItemCount =
    toAmount(cached.initialItemCount) ?? toAmount(cached.itemCount) ?? 0;
  const actualItemCount =
    toAmount(cached.actualItemCount) ?? toAmount(cached.itemCount) ?? 0;
  const initialOrderCompositionAmount =
    toAmount(cached.initialOrderCompositionAmount) ??
    toAmount(cached.orderCompositionAmount) ??
    0;
  const actualOrderCompositionAmount =
    toAmount(cached.actualOrderCompositionAmount) ??
    toAmount(cached.orderCompositionAmount) ??
    0;
  const initialPaymentAmount =
    toAmount(cached.initialPaymentAmount) ??
    toAmount(cached.paymentAmount) ??
    0;
  const actualPaymentAmount =
    toAmount(cached.actualPaymentAmount) ?? toAmount(cached.paymentAmount) ?? 0;
  const orderPaymentAmount =
    toAmount(cached.orderPaymentAmount) ?? actualPaymentAmount;

  return {
    orderCount: toAmount(cached.orderCount) || 1,
    itemCount: toAmount(cached.itemCount) || 0,
    productAmount: actualProductAmount,
    shippingAmount: actualShippingAmount,
    discountAmount: actualDiscountAmount,
    couponAmount: actualCouponAmount,
    orderCompositionAmount: actualOrderCompositionAmount,
    paymentAmount: actualPaymentAmount,

    initialOrderCount,
    actualOrderCount,
    initialItemCount,
    actualItemCount,
    initialProductAmount,
    initialOrderPriceAmount,
    actualProductAmount,
    actualOrderPriceAmount,
    initialShippingAmount,
    actualShippingAmount,
    initialDiscountAmount,
    actualDiscountAmount,
    initialCouponAmount,
    actualCouponAmount,
    initialOrderCompositionAmount,
    actualOrderCompositionAmount,
    initialPaymentAmount,
    actualPaymentAmount,
    orderPaymentAmount,
  };
}

export function getCurrentPaymentAmount(order: any): number {
  return getOrderPaymentSummary(order).paymentAmount;
}

export function getInitialPaymentAmount(order: any): number {
  return getOrderPaymentSummary(order).initialPaymentAmount;
}

export function getActualPaymentAmount(order: any): number {
  return getOrderPaymentSummary(order).actualPaymentAmount;
}

// Backward-compatible alias for callers that explicitly need the initial
// order composition amount (product + shipping - discount - coupon).
export function getOrderCompositionAmount(order: any): number {
  return getOrderPaymentSummary(order).initialOrderCompositionAmount;
}

export function getInitialOrderCompositionAmount(order: any): number {
  return getOrderPaymentSummary(order).initialOrderCompositionAmount;
}

export function getActualOrderCompositionAmount(order: any): number {
  return getOrderPaymentSummary(order).actualOrderCompositionAmount;
}

export function summarizeOrderPayments(orders: any[]): OrderPaymentSummary {
  const summary = (Array.isArray(orders) ? orders : []).reduce(
    (acc, order) => {
      const item = getOrderPaymentSummary(order);
      acc.orderCount += toAmount(item.orderCount) || 0;
      acc.itemCount += toAmount(item.itemCount) || 0;
      acc.productAmount += toAmount(item.productAmount) || 0;
      acc.shippingAmount += toAmount(item.shippingAmount) || 0;
      acc.discountAmount += toAmount(item.discountAmount) || 0;
      acc.couponAmount += toAmount(item.couponAmount) || 0;
      acc.orderCompositionAmount += toAmount(item.orderCompositionAmount) || 0;
      acc.paymentAmount += toAmount(item.paymentAmount) || 0;

      acc.initialOrderCount += toAmount(item.initialOrderCount) || 0;
      acc.actualOrderCount += toAmount(item.actualOrderCount) || 0;
      acc.initialItemCount += toAmount(item.initialItemCount) || 0;
      acc.actualItemCount += toAmount(item.actualItemCount) || 0;
      acc.initialProductAmount += toAmount(item.initialProductAmount) || 0;
      acc.initialOrderPriceAmount +=
        toAmount(item.initialOrderPriceAmount) || 0;
      acc.actualProductAmount += toAmount(item.actualProductAmount) || 0;
      acc.actualOrderPriceAmount += toAmount(item.actualOrderPriceAmount) || 0;
      acc.initialShippingAmount += toAmount(item.initialShippingAmount) || 0;
      acc.actualShippingAmount += toAmount(item.actualShippingAmount) || 0;
      acc.initialDiscountAmount += toAmount(item.initialDiscountAmount) || 0;
      acc.actualDiscountAmount += toAmount(item.actualDiscountAmount) || 0;
      acc.initialCouponAmount += toAmount(item.initialCouponAmount) || 0;
      acc.actualCouponAmount += toAmount(item.actualCouponAmount) || 0;
      acc.initialOrderCompositionAmount +=
        toAmount(item.initialOrderCompositionAmount) || 0;
      acc.actualOrderCompositionAmount +=
        toAmount(item.actualOrderCompositionAmount) || 0;
      acc.initialPaymentAmount += toAmount(item.initialPaymentAmount) || 0;
      acc.actualPaymentAmount += toAmount(item.actualPaymentAmount) || 0;
      acc.orderPaymentAmount += toAmount(item.orderPaymentAmount) || 0;

      return acc;
    },
    { ...EMPTY_SUMMARY }
  );

  return summary;
}
